import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { connectionSlug } from '../agents/tabs/mcpTools';
import McpServerDialog from './McpServerDialog';
import {
  AUTH_MODE_LABELS,
  LegacyInlineMcpConnection,
  McpBinding,
  McpEntry,
  McpServer,
  isMcpBinding,
  useMcpServers,
} from './mcpServerRegistry';

// Cards cap their visible tool list at this many rows; "Show all" reveals the rest.
const VISIBLE_TOOL_LIMIT = 6;

interface McpBindingManagerProps {
  entries: McpEntry[];
  onChange: (entries: McpEntry[]) => void;
  /** Copy for the empty state / picker: what the tools will be used by. */
  consumerLabel: 'agent' | 'service';
  /** Render the "Add MCP Server" button in a header slot instead of inline. */
  renderAddButton?: (open: () => void) => React.ReactNode;
}

interface BindingDraft {
  server: McpServer;
  slug: string;
  enabled_tools: string[];
}

/**
 * Binding manager shared by the agent builder (Agent.tools.mcp) and the
 * service editor (ServiceDefinition.tools.mcp): pick an already-connected
 * org server (or connect a new one in place), choose a slug and a tool
 * subset, and manage per-binding enablement. The slug is editable until the
 * binding is saved into the list and frozen after — it namespaces the LLM
 * tool names skills reference.
 */
export default function McpBindingManager({
  entries,
  onChange,
  consumerLabel,
  renderAddButton,
}: McpBindingManagerProps) {
  const { servers, loading, error, upsert } = useMcpServers();
  const serversById = useMemo(() => new Map(servers.map((server) => [server.id, server])), [servers]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bindingDraft, setBindingDraft] = useState<BindingDraft | null>(null);
  const [bindingError, setBindingError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  const boundServerIds = new Set(entries.filter(isMcpBinding).map((entry) => entry.mcp_server_id));
  const usedSlugs = new Set(
    entries.map((entry) => (isMcpBinding(entry) ? entry.slug : entry.slug || connectionSlug(entry.name)))
  );

  const openPicker = () => {
    setPickerOpen(true);
  };

  const startBinding = (server: McpServer) => {
    // Default slug from the server name; bump with a numeric suffix if the
    // consumer already uses it (two bindings of one server are allowed but
    // must namespace their tools differently).
    let slug = connectionSlug(server.name);
    let attempt = 2;
    while (usedSlugs.has(slug)) {
      slug = `${connectionSlug(server.name).slice(0, 21)}_${attempt}`;
      attempt += 1;
    }
    setBindingDraft({ server, slug, enabled_tools: server.tools.map((tool) => tool.name) });
    setBindingError(null);
    setPickerOpen(false);
  };

  const handleServerCreated = (server: McpServer) => {
    upsert(server);
    startBinding(server);
  };

  const commitBinding = () => {
    if (!bindingDraft) return;
    const slug = connectionSlug(bindingDraft.slug);
    if (!slug || slug !== bindingDraft.slug.trim()) {
      setBindingError('Slug may only contain lowercase letters, digits, and underscores (max 24).');
      return;
    }
    if (usedSlugs.has(slug)) {
      setBindingError(`Slug "${slug}" is already used by another connection on this ${consumerLabel}.`);
      return;
    }
    const all = bindingDraft.server.tools.map((tool) => tool.name);
    const subset = bindingDraft.enabled_tools.filter((name) => all.includes(name));
    const binding: McpBinding = {
      mcp_server_id: bindingDraft.server.id,
      slug,
      enabled: true,
      enabled_tools: subset.length === all.length ? null : subset,
    };
    onChange([...entries, binding]);
    setBindingDraft(null);
  };

  const removeEntry = (index: number) => {
    onChange(entries.filter((_entry, i) => i !== index));
  };

  const setEntryEnabled = (index: number, enabled: boolean) => {
    onChange(entries.map((entry, i) => (i === index ? { ...entry, enabled } : entry)));
  };

  const toggleEntryTool = (index: number, toolName: string, checked: boolean, allNames: string[]) => {
    onChange(
      entries.map((entry, i) => {
        if (i !== index) return entry;
        const current = entry.enabled_tools ?? allNames;
        const next = checked
          ? Array.from(new Set([...current, toolName]))
          : current.filter((name) => name !== toolName);
        return { ...entry, enabled_tools: next };
      })
    );
  };

  const addButton = (
    <Button type="button" variant="outline" size="sm" onClick={openPicker}>
      <Plus className="mr-1.5 h-3.5 w-3.5" />
      Add MCP Server
    </Button>
  );

  const unboundServers = servers.filter((server) => !boundServerIds.has(server.id));

  return (
    <div className="space-y-4">
      {renderAddButton ? renderAddButton(openPicker) : null}

      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1.5">
          <TriangleAlert className="h-3.5 w-3.5" /> {error}
        </p>
      )}

      {entries.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
          <p className="text-sm font-medium">No MCP servers connected</p>
          <p className="text-xs text-muted-foreground max-w-lg mx-auto">
            {consumerLabel === 'agent'
              ? "Give your agent tools from your own product. Connect a server once for your organization, then choose which of its tools this agent can use."
              : 'Give this service tools from your own product. Connect a server once for your organization, then choose which of its tools the service can use.'}
          </p>
          {addButton}
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry, index) => {
            const cardKey = isMcpBinding(entry) ? `${entry.mcp_server_id}:${entry.slug}` : `legacy:${index}`;
            const server = isMcpBinding(entry) ? serversById.get(entry.mcp_server_id) : undefined;
            const legacy = isMcpBinding(entry) ? null : (entry as LegacyInlineMcpConnection);
            const name = server?.name ?? legacy?.name ?? 'Unknown server';
            const url = server?.url ?? legacy?.url ?? '';
            const authMode = server?.auth_mode ?? legacy?.auth_mode ?? 'none';
            const tools = server?.tools ?? legacy?.tools ?? [];
            const allNames = tools.map((tool) => tool.name);
            const enabledTools = (entry.enabled_tools ?? allNames).filter((n) => allNames.includes(n));
            const missing = isMcpBinding(entry) && !loading && !server;
            const showAll = Boolean(expandedCards[cardKey]);
            const visibleTools = showAll || tools.length <= VISIBLE_TOOL_LIMIT ? tools : tools.slice(0, VISIBLE_TOOL_LIMIT);
            const slug = isMcpBinding(entry) ? entry.slug : legacy?.slug || connectionSlug(legacy?.name || '');

            return (
              <div key={cardKey} className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold break-words [overflow-wrap:anywhere]">
                        {missing ? 'Disconnected server' : name}
                      </span>
                      {!missing && (
                        <Badge variant="secondary" className="text-xs font-normal">
                          {AUTH_MODE_LABELS[authMode]}
                        </Badge>
                      )}
                      {server && !server.enabled && (
                        <Badge variant="outline" className="text-xs font-normal text-amber-600 border-amber-300">
                          Disabled for organization
                        </Badge>
                      )}
                      {legacy && (
                        <Badge variant="outline" className="text-xs font-normal">
                          Legacy inline
                        </Badge>
                      )}
                      <span className="font-mono text-xs text-muted-foreground">{slug}_*</span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground truncate">
                      {missing
                        ? 'This server was removed from the organization. Remove this entry or reconnect the server under Integrations → MCP Servers.'
                        : url}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={entry.enabled !== false}
                      onCheckedChange={(checked) => setEntryEnabled(index, checked)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => removeEntry(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {tools.length > 0 && (
                  <div className="space-y-2 border-t pt-3">
                    <p className="text-xs font-medium text-muted-foreground">
                      Tools ({enabledTools.length}/{allNames.length} enabled)
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {visibleTools.map((tool) => (
                        <label
                          key={tool.name}
                          className="flex items-start gap-2 p-2 border rounded-md cursor-pointer hover:bg-muted/40"
                        >
                          <Checkbox
                            checked={enabledTools.includes(tool.name)}
                            className="mt-px"
                            onCheckedChange={(value) =>
                              toggleEntryTool(index, tool.name, Boolean(value), allNames)
                            }
                          />
                          <div className="min-w-0">
                            <span className="block font-mono text-xs break-all leading-4">
                              {tool.name}
                            </span>
                            {tool.description && (
                              <p
                                className="mt-0.5 text-xs text-muted-foreground line-clamp-2"
                                title={tool.description}
                              >
                                {tool.description}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    {tools.length > VISIBLE_TOOL_LIMIT && (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                        onClick={() => setExpandedCards({ ...expandedCards, [cardKey]: !showAll })}
                      >
                        {showAll ? 'Show fewer' : `Show all ${tools.length} tools`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!renderAddButton && <div>{addButton}</div>}
        </div>
      )}

      {/* Picker: already-connected org servers, or connect a new one. */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-lg min-w-0 overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Add MCP Server</DialogTitle>
            <DialogDescription>
              Choose a server already connected to your organization, or connect a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {loading ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading servers…
              </p>
            ) : unboundServers.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {servers.length === 0
                  ? 'No MCP servers are connected to your organization yet.'
                  : `Every connected server is already on this ${consumerLabel}.`}
              </p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto min-w-0">
                {unboundServers.map((server) => (
                  <button
                    key={server.id}
                    type="button"
                    className="w-full min-w-0 text-left rounded-md border p-3 hover:bg-muted/40 disabled:opacity-60"
                    disabled={!server.enabled}
                    onClick={() => startBinding(server)}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <span className="text-sm font-medium min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
                        {server.name}
                      </span>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        <Badge variant="secondary" className="text-xs font-normal">
                          {AUTH_MODE_LABELS[server.auth_mode]}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {server.tools.length} tool{server.tools.length === 1 ? '' : 's'}
                        </span>
                        {!server.enabled && (
                          <span className="text-xs text-amber-600">Disabled</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{server.url}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="sm:justify-between gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPickerOpen(false);
                setCreateOpen(true);
              }}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Connect a new server
            </Button>
            <Button type="button" variant="ghost" onClick={() => setPickerOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <McpServerDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={handleServerCreated} />

      {/* Binding: slug + tool subset for the chosen server. */}
      <Dialog open={Boolean(bindingDraft)} onOpenChange={(open) => !open && setBindingDraft(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden min-w-0">
          {bindingDraft && (
            <>
              <DialogHeader>
                <DialogTitle>Add {bindingDraft.server.name}</DialogTitle>
                <DialogDescription>
                  Choose which tools this {consumerLabel} can use from this server.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mcp-binding-slug">Tool name prefix</Label>
                  <Input
                    id="mcp-binding-slug"
                    value={bindingDraft.slug}
                    onChange={(e) => {
                      setBindingDraft({ ...bindingDraft, slug: e.target.value });
                      setBindingError(null);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Tools appear to the AI as <span className="font-mono">{connectionSlug(bindingDraft.slug) || 'mcp'}_&lt;tool&gt;</span>.
                    The prefix is fixed once added — skills reference tools by these names.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Tools</Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                        onClick={() =>
                          setBindingDraft({
                            ...bindingDraft,
                            enabled_tools: bindingDraft.server.tools.map((tool) => tool.name),
                          })
                        }
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                        onClick={() => setBindingDraft({ ...bindingDraft, enabled_tools: [] })}
                      >
                        None
                      </button>
                    </div>
                  </div>
                  {bindingDraft.server.tools.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      This server exposed no tools at last discovery. Re-discover it under
                      Integrations → MCP Servers.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {bindingDraft.server.tools.map((tool) => (
                        <label
                          key={tool.name}
                          className="flex items-start gap-2 p-2 border rounded-md cursor-pointer hover:bg-muted/40"
                        >
                          <Checkbox
                            checked={bindingDraft.enabled_tools.includes(tool.name)}
                            className="mt-px"
                            onCheckedChange={(value) =>
                              setBindingDraft({
                                ...bindingDraft,
                                enabled_tools: value
                                  ? Array.from(new Set([...bindingDraft.enabled_tools, tool.name]))
                                  : bindingDraft.enabled_tools.filter((name) => name !== tool.name),
                              })
                            }
                          />
                          <div className="min-w-0">
                            <span className="block font-mono text-xs break-all leading-4">
                              {tool.name}
                            </span>
                            {tool.description && (
                              <p
                                className="mt-0.5 text-xs text-muted-foreground line-clamp-2"
                                title={tool.description}
                              >
                                {tool.description}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {bindingError && <p className="text-xs text-red-500">{bindingError}</p>}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setBindingDraft(null)}>
                  Cancel
                </Button>
                <Button type="button" onClick={commitBinding}>
                  Add to {consumerLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
